import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Subscription, take } from 'rxjs';
import { RecoverPinApi } from '../apis';
import { PinEncryptionService } from '../services/pin-encryption.service';
import { Storage } from '../services/storage';
import { TranslationService } from '../services/translation.service';
import { ErrorCode, RecoverPinError } from '../models';
import type { RecoverPinFailureKind, RecoverPinOtpChannel, RecoverPinStep } from '../models';

/** Fallback cooldown before the user may request a fresh OTP. */
const OTP_RESEND_COOLDOWN_SECONDS = 60;

/** A channel offered to the user, with the destination it will deliver to. */
export interface RecoverPinChannelOption {
  id: RecoverPinOtpChannel;
  name: string;
  /** Masked destination, e.g. "••• ••• 846". Null when we cannot derive one. */
  destination: string | null;
  icon: string;
}

/** Icons only — the labels are localised, so they come from the dictionary. */
const CHANNEL_ICONS: Record<RecoverPinOtpChannel, string> = {
  sms: 'ri-message-2-line',
  whatsapp: 'ri-whatsapp-line',
  email: 'ri-mail-line',
};

/**
 * What went wrong, not what to say about it.
 *
 * The store holds the *reason* and the component-facing signals translate it on
 * read. Storing the finished sentence would freeze it in whichever language was
 * active when the error happened — switching locale mid-flow would leave a
 * stale French message on an English screen.
 */
type PasswordErrorState = { kind: 'wrong'; remaining: number | null } | { kind: 'generic' };
type ChannelErrorState = 'none' | 'sendFailed';
/**
 * Why the OTP step is in error. Exported because the dialog reacts to the
 * *reason* rather than to the sentence: only a rejected code should wipe what
 * the user typed.
 */
export type RecoverPinOtpErrorKind = 'invalid' | 'resendFailed';
type PinErrorState = 'encryption';

/** Replace the house `{{name}}` placeholders used across translations.ts. */
function fill(template: string, vars: Record<string, string | number>): string {
  return Object.entries(vars).reduce(
    (out, [key, value]) => out.replaceAll(`{{${key}}}`, String(value)),
    template
  );
}

/** "+22896384846" → "••• ••• 846". Returns null when there is nothing to show. */
function maskPhone(phone: string | undefined): string | null {
  const digits = (phone ?? '').replaceAll(/\D/g, '');
  return digits.length >= 3 ? `••• ••• ${digits.slice(-3)}` : null;
}

/** "arnaud@gozem.co" → "a•••••@gozem.co". Returns null when there is nothing to show. */
function maskEmail(email: string | undefined): string | null {
  const at = (email ?? '').indexOf('@');
  if (at < 1) return null;
  return `${email!.slice(0, 1)}•••••${email!.slice(at)}`;
}

/**
 * Store for the "Recover security code" (PIN reset) flow.
 *
 * Scoped to the dialog — provided in RecoverPinDialog.providers, and reset each
 * time the popup opens so a re-open starts fresh at the password step.
 *
 * Flow (one dialog, steps swapped in place — see {@link RecoverPinStep}):
 *   'password' — validate_password. On success the backend returns the channels
 *                it can deliver an OTP to. Exactly one channel skips the picker.
 *   'channel'  — pick a delivery channel, then generate_pin_recovery_otp.
 *   'otp'      — enter the code. NO REQUEST HAPPENS HERE: the backend has no
 *                verify-OTP endpoint, so the code is only captured and carried
 *                to the final call. Anything else would be theatre.
 *   'newPin'   — enter a new 4-digit code, then pin_recovery (password + OTP +
 *                RSA-encrypted PIN). An invalid or expired OTP surfaces here as
 *                error 047 and routes back to 'otp' — the only way it can.
 *   'success' / 'blocked' / 'failure' / 'discard' — terminal or interstitial,
 *                still inside the same dialog.
 */
@Injectable()
export class RecoverPinStore {
  private readonly api = inject(RecoverPinApi);
  private readonly pinEncryption = inject(PinEncryptionService);
  private readonly storage = inject(Storage);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translation = inject(TranslationService);

  /** This flow's slice of the dictionary, re-read whenever the locale changes. */
  private readonly t = computed(() => this.translation.translations().settings.recoverPin);

  // ── Step ────────────────────────────────────────────────────────────────
  private readonly _step = signal<RecoverPinStep>('password');
  readonly step = computed(() => this._step());

  /** Step to return to when the user cancels a discard or retries after a network error. */
  private readonly _resumeStep = signal<RecoverPinStep>('otp');
  readonly resumeStep = computed(() => this._resumeStep());

  // ── Step 1: password ───────────────────────────────────────────────────
  private readonly _passwordLoading = signal(false);
  private readonly _passwordError = signal<PasswordErrorState | null>(null);
  /** Password is carried through every subsequent call (no token is issued). */
  private readonly _password = signal<string | null>(null);

  readonly passwordLoading = computed(() => this._passwordLoading());
  readonly passwordError = computed<string | null>(() => {
    const error = this._passwordError();
    if (!error) return null;
    const copy = this.t();
    if (error.kind === 'generic') return copy.genericError;
    if (error.remaining == null) return copy.password.wrong;
    const template =
      error.remaining > 1 ? copy.password.wrongRemainingMany : copy.password.wrongRemainingOne;
    return fill(template, { count: error.remaining });
  });

  // ── Step 2: channel ────────────────────────────────────────────────────
  private readonly _otpChannels = signal<RecoverPinOtpChannel[]>([]);
  private readonly _selectedChannel = signal<RecoverPinOtpChannel | null>(null);
  private readonly _channelError = signal<ChannelErrorState | null>(null);
  private readonly _otpSending = signal(false);
  /** True when a single channel let us skip the picker — `back` must skip it too. */
  private readonly _pickerSkipped = signal(false);

  readonly otpChannels = computed(() => this._otpChannels());
  readonly selectedChannel = computed(() => this._selectedChannel());
  readonly channelError = computed<string | null>(() => {
    const error = this._channelError();
    if (!error) return null;
    return error === 'none' ? this.t().channel.none : this.t().channel.sendFailed;
  });
  readonly otpSending = computed(() => this._otpSending());
  readonly pickerSkipped = computed(() => this._pickerSkipped());
  readonly hasChannels = computed(() => this._otpChannels().length > 0);

  /**
   * Channels the backend offered, each with a masked destination.
   *
   * The destination comes from the session in localStorage, not from the API —
   * `validate_password` returns channel names only. Showing "which number" before
   * the user spends an OTP is the difference between a confident tap and a guess.
   */
  readonly channelOptions = computed<RecoverPinChannelOption[]>(() => {
    const user = this.storage.getUserData()?.user;
    const copy = this.t().channel;
    return this._otpChannels()
      .filter((channel) => channel in CHANNEL_ICONS)
      .map((channel) => ({
        id: channel,
        name: copy[channel],
        icon: CHANNEL_ICONS[channel],
        destination: channel === 'email' ? maskEmail(user?.email) : maskPhone(user?.phone),
      }));
  });

  // ── Step 3: OTP ─────────────────────────────────────────────────────────
  private readonly _otpChannel = signal<RecoverPinOtpChannel | null>(null);
  private readonly _otpError = signal<RecoverPinOtpErrorKind | null>(null);
  private readonly _otpResent = signal(false);
  private readonly _otp = signal<string | null>(null);
  private readonly _otpExpiry = signal<string | null>(null);
  private readonly _resendSecondsLeft = signal(0);
  /** The live countdown, held so a new one can cancel it instead of racing it. */
  private resendTimer: Subscription | null = null;

  readonly otpChannel = computed(() => this._otpChannel());
  readonly otpError = computed<string | null>(() => {
    const error = this._otpError();
    if (!error) return null;
    return error === 'invalid' ? this.t().otp.invalid : this.t().otp.resendFailed;
  });
  /**
   * The reason behind {@link otpError}, for callers that must act on it.
   *
   * The dialog clears the digits the user typed when the backend rejects the
   * code (047), but must not when a resend failed: the code from the first send
   * may still be sitting in their messages and still be valid. The rendered
   * sentence cannot tell those two apart, so it is not something to match on.
   */
  readonly otpErrorKind = computed<RecoverPinOtpErrorKind | null>(() => this._otpError());
  readonly otpNotice = computed<string | null>(() =>
    this._otpResent() ? this.t().otp.resent : null
  );
  /** The API's own expiry text, falling back to the localised default. */
  readonly otpExpiry = computed(() => this._otpExpiry() ?? this.t().otp.defaultExpiry);
  readonly resendSecondsLeft = computed(() => this._resendSecondsLeft());
  readonly canResend = computed(() => this._resendSecondsLeft() <= 0 && !this._otpSending());

  // ── Step 4: new PIN ─────────────────────────────────────────────────────
  private readonly _pinError = signal<PinErrorState | null>(null);
  private readonly _submitting = signal(false);

  readonly pinError = computed<string | null>(() =>
    this._pinError() ? this.t().pin.encryptionFailed : null
  );
  readonly submitting = computed(() => this._submitting());

  // ── Terminal states ─────────────────────────────────────────────────────
  private readonly _failureKind = signal<RecoverPinFailureKind>('network');
  readonly failureKind = computed(() => this._failureKind());

  // ── Step 1: verify password ─────────────────────────────────────────────

  verifyPassword(password: string): void {
    if (this._passwordLoading()) return;
    this._passwordLoading.set(true);
    this._passwordError.set(null);

    this.api
      .validatePassword(password)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this._password.set(password);
          const channels = res.otp_channels ?? [];
          this._otpChannels.set(channels);

          if (channels.length === 0) {
            // Nothing to pick and nothing to send — show the dead end on the
            // channel step with a support route, not as a password error.
            this._passwordLoading.set(false);
            this._pickerSkipped.set(false);
            this._channelError.set('none');
            this._step.set('channel');
            return;
          }

          if (channels.length === 1) {
            // A one-option choice is not a choice. Keep the password button
            // spinning and go straight to the code.
            this._pickerSkipped.set(true);
            this._selectedChannel.set(channels[0]);
            this.requestOtp(channels[0], { from: 'password' });
            return;
          }

          this._passwordLoading.set(false);
          this._pickerSkipped.set(false);
          this._selectedChannel.set(null);
          this._channelError.set(null);
          this._step.set('channel');
        },
        error: (err: unknown) => {
          this._passwordLoading.set(false);
          this.applyPasswordError(err);
        },
      });
  }

  // ── Step 2: choose channel + send / resend OTP ─────────────────────────

  selectChannel(channel: RecoverPinOtpChannel): void {
    if (this._otpSending()) return;
    this._selectedChannel.set(channel);
    this._channelError.set(null);
  }

  /** Send the code for the selected channel and move to the OTP step. */
  sendOtp(): void {
    const channel = this._selectedChannel();
    if (!channel || this._otpSending()) return;
    this.requestOtp(channel, { from: 'channel' });
  }

  resendOtp(): void {
    const channel = this._otpChannel();
    if (!channel || !this.canResend()) return;
    this._otpError.set(null);
    this.requestOtp(channel, { from: 'otp' });
  }

  /**
   * The single generate_pin_recovery_otp call site.
   *
   * `from` decides where a failure lands: before the OTP step the user goes to
   * the channel picker to retry, on it the error stays inline so the code they
   * may already have received is not thrown away.
   */
  private requestOtp(
    channel: RecoverPinOtpChannel,
    { from }: { from: 'password' | 'channel' | 'otp' }
  ): void {
    this._otpSending.set(true);
    this._channelError.set(null);
    this._otpResent.set(false);

    this.api
      .generateOtp(this._password() ?? '', channel)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this._otpSending.set(false);
          this._passwordLoading.set(false);
          this._otpChannel.set(channel);
          this._otpExpiry.set(res.otp_expiry_delay?.trim() || null);
          if (from === 'otp') {
            this._otpResent.set(true);
          } else {
            this._otp.set(null);
            this._otpError.set(null);
            this._step.set('otp');
          }
          this.startResendCountdown();
        },
        error: () => {
          this._otpSending.set(false);
          this._passwordLoading.set(false);
          if (from === 'otp') {
            this._otpError.set('resendFailed');
            return;
          }
          this._channelError.set('sendFailed');
          this._step.set('channel');
        },
      });
  }

  /**
   * Restart the resend cooldown.
   *
   * The previous subscription is cancelled first: without that, going back and
   * forward through the flow left several `interval`s alive, all decrementing the
   * same signal, and the cooldown drained at two or three times real time.
   */
  private startResendCountdown(): void {
    this.resendTimer?.unsubscribe();
    this._resendSecondsLeft.set(OTP_RESEND_COOLDOWN_SECONDS);
    this.resendTimer = interval(1000)
      .pipe(take(OTP_RESEND_COOLDOWN_SECONDS), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this._resendSecondsLeft.update((s) => Math.max(0, s - 1)));
  }

  // ── Step 3: capture the OTP ─────────────────────────────────────────────

  /**
   * Store the code and move on. There is deliberately no request here — the
   * backend validates the OTP inside `pin_recovery`, together with the new PIN.
   */
  submitOtp(code: string): void {
    if (code.length !== 4) return;
    this._otp.set(code);
    this._otpError.set(null);
    this._otpResent.set(false);
    this._pinError.set(null);
    this._step.set('newPin');
  }

  // ── Step 4: set the new PIN ─────────────────────────────────────────────

  async submitNewPin(pin: string): Promise<void> {
    const password = this._password();
    const otp = this._otp();
    if (!password || !otp || pin.length !== 4 || this._submitting()) return;

    this._submitting.set(true);
    this._pinError.set(null);

    let encryptedPin: string;
    try {
      encryptedPin = await this.pinEncryption.encryptPin(pin);
    } catch {
      this._submitting.set(false);
      this._pinError.set('encryption');
      return;
    }

    if (!encryptedPin) {
      this._submitting.set(false);
      this._pinError.set('encryption');
      return;
    }

    this.api
      .recoverPin(password, otp, encryptedPin)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this._submitting.set(false);
          this.resendTimer?.unsubscribe();
          this._step.set('success');
        },
        error: (err: unknown) => {
          this._submitting.set(false);
          this.applySubmitError(err);
        },
      });
  }

  // ── Navigation ──────────────────────────────────────────────────────────

  /** Header back arrow. Returns to the previous *visible* step. */
  back(): void {
    switch (this._step()) {
      case 'channel':
        this._channelError.set(null);
        this._step.set('password');
        break;
      case 'otp':
        this._otp.set(null);
        this._otpError.set(null);
        this._otpResent.set(false);
        // The picker was never shown when there was only one channel; going
        // back to it would present a screen the user has never seen.
        this._step.set(this._pickerSkipped() ? 'password' : 'channel');
        break;
      case 'newPin':
        this._pinError.set(null);
        this._step.set('otp');
        break;
      default:
        break;
    }
  }

  /**
   * ✕ / Escape / backdrop. Returns true when the dialog may close outright.
   *
   * From the OTP step onward a code has been spent, so the user is asked first —
   * on a `discard` STEP inside this same dialog, never a second one.
   */
  requestClose(): boolean {
    const step = this._step();
    if (step === 'otp' || step === 'newPin') {
      this._resumeStep.set(step);
      this._step.set('discard');
      return false;
    }
    return step !== 'discard';
  }

  /** "Continuer la réinitialisation" on the discard step. */
  cancelDiscard(): void {
    if (this._step() === 'discard') this._step.set(this._resumeStep());
  }

  /** "Réessayer" after a recoverable network failure. */
  retryAfterFailure(): void {
    if (this._step() === 'failure') this._step.set(this._resumeStep());
  }

  reset(): void {
    this.resendTimer?.unsubscribe();
    this.resendTimer = null;
    this._step.set('password');
    this._resumeStep.set('otp');
    this._passwordLoading.set(false);
    this._passwordError.set(null);
    this._password.set(null);
    this._otpChannels.set([]);
    this._selectedChannel.set(null);
    this._channelError.set(null);
    this._otpSending.set(false);
    this._pickerSkipped.set(false);
    this._otpChannel.set(null);
    this._otpError.set(null);
    this._otpResent.set(false);
    this._otp.set(null);
    this._otpExpiry.set(null);
    this._resendSecondsLeft.set(0);
    this._pinError.set(null);
    this._submitting.set(false);
    this._failureKind.set('network');
  }

  /** Drop the secrets held in memory. Called on close as well as on open. */
  clearSecrets(): void {
    this._password.set(null);
    this._otp.set(null);
  }

  // ── Error mapping ─────────────────────────────────────────────────────────

  /** Route a validate_password rejection to the right step and message. */
  private applyPasswordError(err: unknown): void {
    if (!(err instanceof RecoverPinError)) {
      this._passwordError.set({ kind: 'generic' });
      return;
    }

    if (err.errorCode === ErrorCode.SESSION_EXPIRED) {
      this._failureKind.set('session');
      this._step.set('failure');
      return;
    }

    if (err.errorCode === ErrorCode.INVALID_PASSWORD) {
      if (err.blockTime && err.blockTime > 0) {
        this._step.set('blocked');
        return;
      }
      this._passwordError.set({ kind: 'wrong', remaining: err.remainingAttempts });
      return;
    }

    this._passwordError.set({ kind: 'generic' });
  }

  /**
   * Route a pin_recovery rejection.
   *
   * 047 is the ONLY code that can send the user backwards: it means the OTP was
   * wrong or expired, and since nothing verified it earlier this is the first
   * moment anyone could know.
   */
  private applySubmitError(err: unknown): void {
    if (err instanceof RecoverPinError && err.errorCode === ErrorCode.INVALID_OTP) {
      this._otp.set(null);
      this._otpError.set('invalid');
      this._step.set('otp');
      return;
    }

    if (err instanceof RecoverPinError && err.errorCode === ErrorCode.SESSION_EXPIRED) {
      this._failureKind.set('session');
      this._step.set('failure');
      return;
    }

    // Anything else: the PIN was not changed, so the current one still works.
    // Say so on a dedicated step with a retry rather than a dead-end message.
    this._resumeStep.set('newPin');
    this._failureKind.set('network');
    this._step.set('failure');
  }
}
